<div class="container-fluid p-3 ti">

  <h4 class="m-0 mb-3 text-color fs-24 fg-500">{{ 'TEST_IMPORT.TITLE' | translate }}</h4>

  <!-- Fil des étapes -->
  <ol class="ti__steps mb-4">
    <li [class.ti__step--active]="step() === 'source'" [class.ti__step--done]="step() !== 'source'">
      <span class="ti__step-num">1</span> {{ 'TEST_IMPORT.STEP_SOURCE' | translate }}
    </li>
    <li [class.ti__step--active]="step() === 'mapping'" [class.ti__step--done]="step() === 'review' || step() === 'done'">
      <span class="ti__step-num">2</span> {{ 'TEST_IMPORT.STEP_MAPPING' | translate }}
    </li>
    <li [class.ti__step--active]="step() === 'review'" [class.ti__step--done]="step() === 'done'">
      <span class="ti__step-num">3</span> {{ 'TEST_IMPORT.STEP_REVIEW' | translate }}
    </li>
    <li [class.ti__step--active]="step() === 'done'">
      <span class="ti__step-num">4</span> {{ 'TEST_IMPORT.STEP_DONE' | translate }}
    </li>
  </ol>

  <!-- ══ Étape 1 : la source ══════════════════════════════════════ -->
  @if (step() === 'source') {
    <div class="card ti__card">
      <div class="card-body">

        <div class="row g-3">
          <!-- Un seul endroit pour désigner une destination : un parcours, sa
               matière, son titulaire, puis « ajouter ». L'écran demandait le
               couple parcours/matière en tête, les parcours « supplémentaires »
               plus bas, et le professeur ailleurs encore — trois endroits pour
               un même geste, et le premier parcours sans titulaire propre. -->
          <div class="col-12">
            <div class="ti__targets">
              <div class="ti__targets-head">
                <i class="fa-solid fa-layer-group" aria-hidden="true"></i>
                <span>{{ 'TEST_IMPORT.TARGETS_TITLE' | translate }}</span>
              </div>
              <p class="ti__targets-hint">{{ 'TEST_IMPORT.TARGETS_HINT' | translate }}</p>

              <div class="ti__target-add">
                <select class="form-select form-select-sm"
                        [attr.aria-label]="'ADMIN.SELECT_PATH' | translate"
                        [ngModel]="targetPathId()"
                        (ngModelChange)="onTargetPathChange($event)">
                  <option [ngValue]="null">{{ 'ADMIN.SELECT_PATH' | translate }}</option>
                  @for (p of availableTargetPaths(); track p.id) {
                    <option [ngValue]="p.id">{{ p.title }}</option>
                  }
                </select>

                <select class="form-select form-select-sm"
                        [attr.aria-label]="'ADMIN.SELECT_SUBJECT' | translate"
                        [ngModel]="targetSubjectId()"
                        (ngModelChange)="onTargetSubjectChange($event)"
                        [disabled]="targetSubjects().length === 0">
                  <option [ngValue]="null">{{ 'ADMIN.SELECT_SUBJECT' | translate }}</option>
                  @for (s of targetSubjects(); track s.id) {
                    <option [ngValue]="s.id">{{ s.name }}</option>
                  }
                </select>

                <!-- Un test appartient au professeur qui l'enseignera. Sans
                     titulaire, la matière reçoit ses questions mais aucun test
                     n'y est composé — autant le choisir ici. -->
                @if (isAdmin) {
                  <select class="form-select form-select-sm"
                          [attr.aria-label]="'TEST_IMPORT.PROFESSOR' | translate"
                          [ngModel]="targetProfessorId()"
                          (ngModelChange)="targetProfessorId.set($event)"
                          [disabled]="!targetSubjectId()">
                    <option [ngValue]="null">{{ 'TEST_IMPORT.PROFESSOR_NONE' | translate }}</option>
                    @for (p of targetProfessors(); track p.id) {
                      <option [ngValue]="+p.id">{{ professorLabel(p) }}</option>
                    }
                  </select>
                }

                <button type="button" class="btn btn-sm btn-outline-primary"
                        [disabled]="!canAddTarget()" (click)="addTarget()">
                  <i class="fa-solid fa-plus me-1" aria-hidden="true"></i>
                  {{ 'BUTTONS.ADD' | translate }}
                </button>
              </div>

              @if (isAdmin) {
                <small class="text-muted d-block mb-2">
                  @if (targetSubjectId() && targetProfessors().length === 0) {
                    {{ 'TEST_IMPORT.PROFESSOR_EMPTY' | translate }}
                  } @else {
                    {{ 'TEST_IMPORT.PROFESSOR_HINT' | translate }}
                  }
                </small>
              }

              <!-- Rien de retenu : l'import n'a pas de destination et l'étape
                   suivante reste inerte. Le dire ici évite d'en chercher la
                   raison sur un bouton grisé. -->
              @if (chosenTargets().length === 0) {
                <p class="ti__targets-required">
                  <i class="fa-solid fa-circle-info" aria-hidden="true"></i>
                  {{ 'TEST_IMPORT.TARGETS_REQUIRED' | translate }}
                </p>
              }

              @for (t of chosenTargets(); track t.subjectId) {
                <div class="ti__target" [class.ti__target--off]="isOffCycle(t)">
                  <span class="ti__target-path">{{ t.pathTitle }}</span>
                  <span class="ti__target-subject">{{ t.subjectName }}</span>

                  <!-- Sans titulaire, la matière reçoit ses questions mais
                       aucun test : le dire ici évite de le découvrir au bilan. -->
                  @if (t.professorName) {
                    <span class="ti__target-prof">{{ t.professorName }}</span>
                  } @else if (composesTest()) {
                    <span class="ti__target-warn">
                      <i class="fa-solid fa-circle-exclamation" aria-hidden="true"></i>
                      {{ 'TEST_IMPORT.NO_OWNER_BANK_ONLY' | translate }}
                    </span>
                  }

                  <!-- Un autre cycle n'est pas refusé, il est signalé : verser
                       un programme de primaire dans un lycée reste possible,
                       mais ne doit pas passer inaperçu. -->
                  @if (isOffCycle(t)) {
                    <span class="ti__target-warn">
                      <i class="fa-solid fa-circle-exclamation" aria-hidden="true"></i>
                      {{ 'TEST_IMPORT.OTHER_CYCLE' | translate }}
                    </span>
                  }

                  <button type="button" class="btn btn-sm ti__target-remove"
                          [attr.aria-label]="'BUTTONS.REMOVE' | translate"
                          (click)="removeTarget(t.subjectId)">
                    <i class="fa-solid fa-xmark" aria-hidden="true"></i>
                  </button>
                </div>
              }
            </div>
          </div>


          <div class="col-md-3">
            <label class="form-label" for="ti-lang">{{ 'TEST_IMPORT.LANGUAGE' | translate }}</label>
            <select id="ti-lang" class="form-select"
                    [ngModel]="language()" (ngModelChange)="language.set($event)">
              <option value="ar">العربية</option>
              <option value="fr">Français</option>
              <option value="en">English</option>
            </select>
          </div>

          <div class="col-md-3">
            <label class="form-label" for="ti-dir">{{ 'TEST_IMPORT.DIRECTION' | translate }}</label>
            <select id="ti-dir" class="form-select"
                    [ngModel]="direction()" (ngModelChange)="direction.set($event)">
              <option value="rtl">{{ 'TEST_IMPORT.RTL' | translate }}</option>
              <option value="ltr">{{ 'TEST_IMPORT.LTR' | translate }}</option>
            </select>
          </div>

          <div class="col-md-6">
            <label class="form-label" for="ti-file">{{ 'TEST_IMPORT.FILE' | translate }}</label>
            <input id="ti-file" type="file" class="form-control"
                   accept=".docx,.pdf" (change)="onFileSelected($event)">
            <small class="text-muted">{{ 'TEST_IMPORT.FILE_HINT' | translate }}</small>
          </div>
        </div>

        @if (uploadPercent() !== null) {
          <div class="progress mt-3" role="progressbar" [attr.aria-valuenow]="uploadPercent()"
               aria-valuemin="0" aria-valuemax="100">
            <div class="progress-bar" [style.width.%]="uploadPercent()">{{ uploadPercent() }}%</div>
          </div>
        }

        <div class="d-flex justify-content-end mt-4">
          <button type="button" class="btn btn-primary px-4"
                  [disabled]="!canAnalyze()" (click)="analyze()">
            @if (analyzing()) { <i class="fa-solid fa-spinner fa-spin me-2"></i> }
            {{ 'TEST_IMPORT.ANALYZE' | translate }}
          </button>
        </div>

      </div>
    </div>
  }

  <!-- ══ Étape 2 : le mapping ═════════════════════════════════════ -->
  @if (step() === 'mapping' && preview(); as p) {
    <div class="card ti__card">
      <div class="card-body">

        <div class="d-flex flex-wrap gap-3 align-items-center mb-3">
          <span class="badge bg-secondary">{{ p.format | uppercase }}</span>
          <span class="text-muted small">
            {{ 'TEST_IMPORT.ROWS_FOUND' | translate:{ count: p.rows.length, pages: p.pagesProcessed } }}
          </span>
          @if (p.confidence === 'Approximate') {
            <span class="badge bg-warning-subtle text-warning-emphasis">
              <i class="fa-solid fa-triangle-exclamation me-1"></i>
              {{ 'TEST_IMPORT.APPROXIMATE' | translate }}
            </span>
          }
        </div>

        @for (w of p.warnings; track w) {
          <div class="alert alert-warning py-2 small">{{ w }}</div>
        }

        <p class="text-muted small">{{ 'TEST_IMPORT.MAPPING_HINT' | translate }}</p>

        <div class="table-responsive ti__table-wrap">
          <table class="table table-sm table-bordered align-middle ti__table"
                 [dir]="direction()">
            <thead>
              <tr>
                @for (c of columns(); track c) {
                  <th>
                    <select class="form-select form-select-sm mb-1"
                            [value]="roleOf(c)"
                            (change)="onRoleChange(c, $any($event.target).value)">
                      @for (opt of roleOptions; track opt.value) {
                        <option [value]="opt.value">{{ opt.labelKey | translate }}</option>
                      }
                    </select>
                    @if (p.headerLabels[c]) {
                      <span class="ti__header-label">{{ p.headerLabels[c] }}</span>
                    }
                  </th>
                }
              </tr>
            </thead>
            <tbody>
              @for (row of sampleRows(); track row.rowNumber) {
                <tr>
                  @for (c of columns(); track c) {
                    <td [class.ti__cell--green]="hasHighlight(row, c)">
                      <app-math-text [text]="cellOf(row, c)"></app-math-text>
                    </td>
                  }
                </tr>
              }
            </tbody>
          </table>
        </div>

        @if (!hasQuestionColumn()) {
          <div class="alert alert-danger py-2 small mt-2">
            {{ 'TEST_IMPORT.QUESTION_REQUIRED' | translate }}
          </div>
        }

        @if (!hasThemeColumn()) {
          <div class="alert alert-danger py-2 small mt-2">
            {{ 'TEST_IMPORT.THEME_REQUIRED' | translate }}
          </div>
        }

        <div class="d-flex justify-content-between mt-4">
          <button type="button" class="btn btn-secondary px-4" (click)="back()">
            {{ 'COMMON.BACK' | translate }}
          </button>
          <button type="button" class="btn btn-primary px-4"
                  [disabled]="!mappingValid() || projecting()" (click)="project()">
            @if (projecting()) { <i class="fa-solid fa-spinner fa-spin me-2"></i> }
            {{ 'TEST_IMPORT.PREVIEW' | translate }}
          </button>
        </div>

      </div>
    </div>
  }

  <!-- ══ Étape 3 : la relecture ═══════════════════════════════════ -->
  @if (step() === 'review') {
    <div class="card ti__card">
      <div class="card-body">

        <div class="d-flex flex-wrap gap-2 mb-3">
          <span class="badge bg-success-subtle text-success-emphasis">
            {{ 'TEST_IMPORT.COUNT_IMPORTABLE' | translate:{ count: importable().length } }}
          </span>
          @if (duplicates().length > 0) {
            <span class="badge bg-secondary-subtle text-secondary-emphasis">
              {{ 'TEST_IMPORT.COUNT_DUPLICATES' | translate:{ count: duplicates().length } }}
            </span>
          }
          @if (withoutCorrect().length > 0) {
            <span class="badge bg-warning-subtle text-warning-emphasis">
              {{ 'TEST_IMPORT.COUNT_NO_ANSWER' | translate:{ count: withoutCorrect().length } }}
            </span>
          }
        </div>

        <!-- Réglages du test : le formulaire de la création manuelle, tel quel.
             Recopier les champs ici aurait laissé les deux écrans diverger — un
             test composé à l'import n'a aucune raison d'avoir moins de réglages
             qu'un test saisi à la main. Le titre y garde sa règle propre :
             laissé vide, les questions rejoignent la seule banque. -->
        <div class="ti__settings mb-4">
          <p class="ti__settings-title">{{ 'TEST_IMPORT.SETTINGS_TITLE' | translate }}</p>

          <app-test-info-form
            [initialData]="testFormSeed()"
            (formReady)="onTestFormReady($event)">
          </app-test-info-form>

          <p class="text-muted small mt-3 mb-0">
            @if (composesTest()) {
              {{ 'TEST_IMPORT.SETTINGS_TIME_HINT' | translate:{ count: importable().length, minutes: suggestedMinutes() } }}
              {{ 'TEST_IMPORT.SETTINGS_POINTS_HINT' | translate:{ total: importable().length } }}
            } @else {
              {{ 'TEST_IMPORT.TEST_TITLE_HINT' | translate }}
            }
          </p>
        </div>

        <!-- Un test composé se relit comme un tout : en-tête, compteurs, puis
             les questions dépliables. Sans titre de test, il n'y a pas de test —
             les questions rejoignent la banque, et la liste plate reste la bonne
             présentation. -->
        @if (composesTest() && targetPreviews().length) {
          <div class="ti__tests" [dir]="direction()">
            @for (t of targetPreviews(); track t.subjectId; let i = $index) {
              <section class="ti__test" [class.ti__test--open]="isTargetExpanded(i)">

                <button type="button" class="ti__test-head"
                        [attr.aria-expanded]="isTargetExpanded(i)"
                        (click)="toggleTargetBlock(i)">
                  <i class="fa-solid ti__test-chevron"
                     [class.fa-chevron-down]="isTargetExpanded(i)"
                     [class.fa-chevron-right]="!isTargetExpanded(i)"
                     aria-hidden="true"></i>

                  <span class="ti__test-name">{{ testInfo()?.['title'] || ('TEST_IMPORT.SETTINGS_TITLE' | translate) }}</span>

                  <span class="ti__test-where">
                    {{ t.learningPathTitle || '—' }} · {{ t.subjectName || '—' }}
                  </span>

                  <span class="ti__test-counts">
                    <!-- Quand un test est composé, le chiffre qui compte est ce
                         qu'il posera — pas ce qui s'écrit en banque. -->
                    <span class="badge bg-success-subtle text-success-emphasis">
                      @if (composesTest()) {
                        {{ 'TEST_IMPORT.COUNT_IN_TEST' | translate:{ count: retainedCountOf(i) } }}
                      } @else {
                        {{ 'TEST_IMPORT.COUNT_IMPORTABLE' | translate:{ count: createdCountOf(i) } }}
                      }
                    </span>
                    @if (duplicateCountOf(i) > 0) {
                      <span class="badge bg-secondary-subtle text-secondary-emphasis">
                        {{ 'TEST_IMPORT.COUNT_DUPLICATES' | translate:{ count: duplicateCountOf(i) } }}
                      </span>
                    }

                    <!-- Le fichier se répète : ces lignes ne produisent rien de
                         plus, et le dire évite de chercher les questions
                         manquantes dans le test composé. -->
                    @if (repeatCountOf(i) > 0) {
                      <span class="badge bg-warning-subtle text-warning-emphasis">
                        {{ 'TEST_IMPORT.COUNT_REPEATS' | translate:{ count: repeatCountOf(i) } }}
                      </span>
                    }
                  </span>
                </button>

                @if (isTargetExpanded(i)) {
                  <!-- Les corrections se font une fois, sur le bloc du dépôt :
                       le fichier est le même pour toutes les cibles. -->
                  <div class="ti__test-body">
                    @if (!isEditableBlock(i)) {
                      <p class="ti__test-readonly">{{ 'TEST_IMPORT.READ_ONLY_TARGET' | translate }}</p>
                    }
                    <ng-container
                      *ngTemplateOutlet="questionList; context: { $implicit: questionsOf(i), editable: isEditableBlock(i) }">
                    </ng-container>
                  </div>
                }
              </section>
            }
          </div>
        } @else {
          <ng-container
            *ngTemplateOutlet="questionList; context: { $implicit: questions(), editable: true }">
          </ng-container>
        }

        <ng-template #questionList let-list let-editable="editable">
        <div class="ti__questions" [dir]="direction()">
          @for (q of list; track q.rowNumber) {
            <div class="ti__question" [class.ti__question--muted]="!!q.duplicateOfQuestionId">

              <div class="ti__question-head">
                <span class="ti__row-num">{{ 'TEST_IMPORT.ROW' | translate:{ n: q.rowNumber, page: q.pageNumber } }}</span>
                @if (q.theme) { <span class="badge bg-light text-dark border">{{ q.theme }}</span> }
                @if (q.duplicateOfQuestionId) {
                  <span class="badge bg-secondary">{{ 'TEST_IMPORT.DUPLICATE' | translate }}</span>
                }
                @if (isEdited(q)) {
                  <span class="badge bg-primary-subtle text-primary-emphasis border">{{ 'TEST_IMPORT.EDITED' | translate }}</span>
                }

                <!-- La correction ne se fait que sur la matiere de depot : le
                     fichier est le meme partout, et le serveur applique la
                     correction a chaque cible. -->
                @if (editable && !isEditing(q)) {
                  <button type="button" class="ti__edit-btn" (click)="startEdit(q)">
                    <i class="fa-solid fa-pen"></i>
                    <span>{{ 'COMMON.EDIT' | translate }}</span>
                  </button>
                }
              </div>

              @if (editable && isEditing(q) && draft(); as d) {

                <div class="ti__edit">
                  <label class="ti__edit-label" [attr.for]="'ti-title-' + q.rowNumber">
                    {{ 'TEST_IMPORT.ROLE.QUESTION' | translate }}
                  </label>
                  <app-rich-field>
                    <textarea class="form-control" rows="3"
                              [id]="'ti-title-' + q.rowNumber"
                              [maxlength]="titleMax"
                              [ngModel]="d.title"
                              [ngModelOptions]="{ standalone: true }"
                              (ngModelChange)="setDraftTitle($event)"></textarea>
                  </app-rich-field>
                  @if (!draftValid()) {
                    <div class="ti__edit-error">
                      <i class="fa-solid fa-triangle-exclamation me-1"></i>{{ 'TEST_IMPORT.EDIT_TITLE_REQUIRED' | translate }}
                    </div>
                  }

                  <div class="ti__edit-label">{{ 'TEST_IMPORT.EDIT_ANSWERS' | translate }}</div>
                  <ul class="ti__answers ti__answers--edit">
                    @for (a of d.answers; track $index) {
                      <li [class.ti__answer--correct]="a.isCorrect">
                        <button type="button" class="ti__answer-btn"
                                [title]="'TEST_IMPORT.MARK_CORRECT' | translate"
                                (click)="setDraftCorrect($index)">
                          <i class="fa-solid" [class.fa-circle-check]="a.isCorrect"
                             [class.fa-circle]="!a.isCorrect"></i>
                        </button>
                        <app-rich-field class="ti__answer-rich" [compact]="true">
                          <input type="text" class="form-control form-control-sm"
                                 [ngModel]="a.content"
                                 [ngModelOptions]="{ standalone: true }"
                                 (ngModelChange)="setDraftAnswer($index, $event)">
                        </app-rich-field>
                        <button type="button" class="ti__answer-del"
                                [title]="'TEST_IMPORT.ANSWER_REMOVE' | translate"
                                (click)="removeDraftAnswer($index)">
                          <i class="fa-solid fa-xmark"></i>
                        </button>
                      </li>
                    }
                  </ul>

                  <button type="button" class="btn btn-sm btn-outline-secondary ti__answer-add"
                          (click)="addDraftAnswer()">
                    <i class="fa-solid fa-plus me-1"></i>{{ 'TEST_IMPORT.ANSWER_ADD' | translate }}
                  </button>

                  <div class="ti__edit-fields">
                    @for (f of draftFields; track f.name) {
                      <label class="ti__edit-field">
                        <span>{{ f.labelKey | translate }}</span>
                        <input type="text" class="form-control form-control-sm"
                               [attr.maxlength]="f.max ?? null"
                               [ngModel]="fieldValue(f.name)"
                               [ngModelOptions]="{ standalone: true }"
                               (ngModelChange)="setDraftField(f.name, $event)">
                      </label>
                    }
                  </div>

                  <p class="ti__edit-note">
                    <i class="fa-solid fa-circle-info me-1"></i>{{ 'TEST_IMPORT.EDIT_NOTE' | translate }}
                  </p>

                  <div class="ti__edit-actions">
                    <button type="button" class="btn btn-sm btn-secondary" (click)="cancelEdit()">
                      {{ 'COMMON.CANCEL' | translate }}
                    </button>
                    <button type="button" class="btn btn-sm btn-primary"
                            [disabled]="!draftValid()" (click)="saveEdit()">
                      {{ 'COMMON.SAVE' | translate }}
                    </button>
                  </div>
                </div>

              } @else {

                <p class="ti__question-title"><app-math-text [text]="q.title"></app-math-text></p>

                <ul class="ti__answers">
                  @for (a of q.answers; track $index) {
                    <li [class.ti__answer--correct]="a.isCorrect">
                      <button type="button" class="ti__answer-btn"
                              [disabled]="!editable"
                              [title]="'TEST_IMPORT.MARK_CORRECT' | translate"
                              (click)="markCorrect(q, $index)">
                        <i class="fa-solid" [class.fa-circle-check]="a.isCorrect"
                           [class.fa-circle]="!a.isCorrect"></i>
                      </button>
                      <span><app-math-text [text]="a.content"></app-math-text></span>
                    </li>
                  }
                </ul>

                @for (w of q.warnings; track w) {
                  <div class="ti__warning"><i class="fa-solid fa-triangle-exclamation me-1"></i>{{ w }}</div>
                }

              }

            </div>
          }
        </div>
        </ng-template>

        <!-- Le test composé aura un titulaire ; sans lui l'API refuse de le
             créer, et l'import échouerait après coup. -->
        @if (needsProfessor()) {
          <div class="alert alert-warning py-2 small mt-3">
            <i class="fa-solid fa-triangle-exclamation me-1"></i>
            {{ 'TEST_IMPORT.PROFESSOR_REQUIRED' | translate }}
          </div>
        }

        <!-- L'autre raison d'un bouton inerte. Une seule était dite : quand le
             fichier ne produisait rien d'exploitable, le bouton restait gris et
             l'écran se taisait. -->
        @if (!needsProfessor() && retained().length === 0) {
          <div class="alert alert-warning py-2 small mt-3">
            <i class="fa-solid fa-triangle-exclamation me-1"></i>
            {{ 'TEST_IMPORT.NOTHING_TO_COMMIT' | translate }}
          </div>
        }

        <!-- Tout le fichier est deja dans la matiere : c'est la signature d'un
             redepot du meme document, et l'API le refuse. La case n'apparait
             que la, parce que c'est la seule fois ou elle veut dire quelque
             chose. -->
        @if (allAlreadyInBank()) {
          <div class="alert alert-warning py-2 small mt-3">
            <i class="fa-solid fa-triangle-exclamation me-1"></i>
            {{ 'TEST_IMPORT.ALREADY_IN_BANK' | translate:{ count: retained().length } }}

            <div class="form-check mt-2 mb-0">
              <input class="form-check-input" type="checkbox" id="ti-allow-duplicate"
                     [ngModel]="allowDuplicateImport()"
                     [ngModelOptions]="{ standalone: true }"
                     (ngModelChange)="allowDuplicateImport.set($event)">
              <label class="form-check-label" for="ti-allow-duplicate">
                {{ 'TEST_IMPORT.IMPORT_ANYWAY' | translate }}
              </label>
            </div>
          </div>
        }

        <div class="d-flex justify-content-between mt-4">
          <button type="button" class="btn btn-secondary px-4" (click)="back()">
            {{ 'COMMON.BACK' | translate }}
          </button>
          <button type="button" class="btn btn-primary px-4"
                  [disabled]="!canCommit()" (click)="commit()">
            @if (committing()) { <i class="fa-solid fa-spinner fa-spin me-2"></i> }
            {{ 'TEST_IMPORT.COMMIT' | translate }}
          </button>
        </div>

      </div>
    </div>
  }

  <!-- ══ Étape 4 : le bilan ═══════════════════════════════════════ -->
  @if (step() === 'done' && result(); as r) {
    <div class="card ti__card">
      <div class="card-body text-center">

        <i class="fa-solid fa-circle-check ti__done-icon"></i>
        <h5 class="mt-3">{{ 'TEST_IMPORT.DONE_TITLE' | translate }}</h5>

        <div class="ti__stats">
          <div><strong>{{ r.questionsCreated }}</strong><span>{{ 'TEST_IMPORT.STAT_QUESTIONS' | translate }}</span></div>
          <div><strong>{{ r.themesCreated }}</strong><span>{{ 'TEST_IMPORT.STAT_THEMES' | translate }}</span></div>
          <div><strong>{{ r.competenciesCreated }}</strong><span>{{ 'TEST_IMPORT.STAT_COMPETENCIES' | translate }}</span></div>
          <div><strong>{{ r.gradeLevelsCreated }}</strong><span>{{ 'TEST_IMPORT.STAT_LEVELS' | translate }}</span></div>
          <div><strong>{{ r.prerequisitesLinked }}</strong><span>{{ 'TEST_IMPORT.STAT_PREREQUISITES' | translate }}</span></div>
          @if (r.questionsSkipped > 0) {
            <div><strong>{{ r.questionsSkipped }}</strong><span>{{ 'TEST_IMPORT.STAT_SKIPPED' | translate }}</span></div>
          }
        </div>

        <!-- Le détail par parcours, dès qu'il y en a plus d'un : un total dirait
             « 120 questions créées » sans dire où, et masquerait le parcours où
             rien n'est passé. -->
        @if ((r.targets?.length ?? 0) > 1) {
          <ul class="ti__target-results">
            @for (t of r.targets; track t.subjectId) {
              <li>
                @if (t.alreadyImported) {
                  {{ 'TEST_IMPORT.TARGET_ALREADY' | translate: {
                       path: t.learningPathTitle || t.subjectName || ('#' + t.subjectId)
                     } }}
                } @else {
                  {{ 'TEST_IMPORT.TARGET_RESULT' | translate: {
                       path: t.learningPathTitle || t.subjectName || ('#' + t.subjectId),
                       created: t.questionsCreated,
                       skipped: t.questionsSkipped
                     } }}
                }
                @if (t.bankOnly) {
                  <span class="ti__target-warn ms-2">
                    <i class="fa-solid fa-circle-exclamation" aria-hidden="true"></i>
                    {{ 'TEST_IMPORT.NO_OWNER_BANK_ONLY' | translate }}
                  </span>
                }
              </li>
            }
          </ul>
        }

        <p class="text-muted small mt-3">{{ 'TEST_IMPORT.DONE_NOTE' | translate }}</p>

        @for (w of r.warnings; track w) {
          <div class="alert alert-warning py-2 small text-start">{{ w }}</div>
        }

        <div class="d-flex justify-content-center gap-2 mt-3">
          <button type="button" class="btn btn-secondary px-4" (click)="restart()">
            {{ 'TEST_IMPORT.IMPORT_ANOTHER' | translate }}
          </button>
          <button type="button" class="btn btn-primary px-4" (click)="goToBank()">
            {{ 'TEST_IMPORT.GO_TO_BANK' | translate }}
          </button>
        </div>

      </div>
    </div>
  }

</div>
